import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { fetchProject, listProjectPlans } from '@/api/tasks';
import { InterventieTaskPhotoSection } from '@/components/interventie/InterventieTaskPhotoSection';
import { InterventieBadge } from '@/components/interventie/InterventieBadge';
import { SafeHtml } from '@/components/ui/SafeHtml';
import { descriptionHasContent } from '@/utils/descriptionSanitize';
import type { PhotoItem, ProjectDetail, ProjectTask } from '@/types/odoo';

function findNearestFutureTask(tasks: ProjectTask[]): number | null {
  const pad = (n: number) => String(n).padStart(2, '0');
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  const future = tasks
    .filter(t => t.date_deadline && String(t.date_deadline).slice(0, 10) >= todayStr)
    .sort((a, b) => String(a.date_deadline).localeCompare(String(b.date_deadline)));
  return future[0]?.id ?? tasks[0]?.id ?? null;
}

export function InterventieProjectView() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    projectName: routeProjectName,
    werfLocation,
  } = (location.state as { projectName?: string; werfLocation?: string }) || {};
  const urlTaskId = searchParams.get('task') ? Number(searchParams.get('task')) : null;
  const pid = Number(projectId);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [plans, setPlans] = useState<PhotoItem[]>([]);

  useEffect(() => {
    if (!pid) return;
    setLoading(true);
    setError(null);

    fetchProject(pid)
      .then((detail) => {
        setProjectDetail(detail);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Project ophalen mislukt');
        setLoading(false);
      });
  }, [pid]);

  useEffect(() => {
    if (!pid) return;
    listProjectPlans(pid)
      .then((data) => setPlans(data.plans))
      .catch(() => {}); // silently fail
  }, [pid]);

  // Resolve taskId from URL or auto-select nearest future task
  const [routeTaskId, setRouteTaskId] = useState<number | undefined>(urlTaskId ?? undefined);
  useEffect(() => {
    if (urlTaskId) {
      setRouteTaskId(urlTaskId);
    } else if (projectDetail) {
      const nearest = findNearestFutureTask(projectDetail.projectTasks);
      if (nearest) {
        setRouteTaskId(nearest);
        setSearchParams({ task: String(nearest) }, { replace: true });
      }
    }
  }, [urlTaskId, projectDetail, setSearchParams]);

  // Look up the routed task from project data. A tagged (Interventie) task puts
  // its photos first, above project info and Taakomschrijving.
  const routeTask = projectDetail && routeTaskId
    ? projectDetail.projectTasks.find(t => t.id === routeTaskId)
    : undefined;
  const routeTaskName = routeTask?.name;
  const photosFirst = Boolean(routeTask?.isInterventie);

  if (loading) {
    return (
      <div className="px-4 py-5 max-w-lg lg:max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse" />
          <div className="flex-1">
            <div className="h-5 bg-gray-200 rounded w-3/4 mb-1 animate-pulse" />
            <div className="h-4 bg-gray-100 rounded w-1/2 animate-pulse" />
          </div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-2/3 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-1/3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-5 max-w-lg lg:max-w-4xl mx-auto">
        <button
          type="button"
          onClick={() => navigate('/interventie')}
          className="flex items-center gap-2 text-td-blue text-sm font-medium mb-4 min-h-[48px]"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Terug
        </button>
        <div className="bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
          <p className="font-semibold">Fout bij laden</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-5 max-w-lg lg:max-w-4xl mx-auto">
      {/* Header with back button */}
      <div className="flex items-start gap-3 mb-5">
        <button
          type="button"
          onClick={() => navigate('/interventie')}
          className="shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-white border border-gray-200 hover:bg-gray-50 active:bg-gray-100 transition-colors"
          aria-label="Terug naar interventies"
        >
          <svg className="w-5 h-5 text-td-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="min-w-0">
          <h1 className="text-lg font-extrabold text-td-navy truncate">
            {routeProjectName || projectDetail?.projectName || `Project ${projectId}`}
            {photosFirst && <InterventieBadge className="ml-2 align-middle" />}
          </h1>
          {werfLocation && (
            <p className="text-xs text-td-muted mt-0.5 truncate">{werfLocation}</p>
          )}
        </div>
      </div>

      {photosFirst && routeTaskId && <InterventieTaskPhotoSection taskId={routeTaskId} />}

      {/* Project info + Task descriptions (2-col on desktop) */}
      {projectDetail && (
        <div className="lg:grid lg:grid-cols-2 lg:gap-4">
          {/* Project info card */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 lg:mb-0">
            <h2 className="text-xs font-bold text-td-muted uppercase tracking-wide mb-2">Projectinfo</h2>
            <dl className="grid grid-cols-2 gap-3">
              <div>
                <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Project</dt>
                <dd className="text-sm font-bold text-td-navy mt-0.5">{projectDetail.projectName}</dd>
              </div>
              <div>
                <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">S-nummer</dt>
                <dd className="text-sm font-bold text-td-blue mt-0.5">{projectDetail.saleOrder?.name ?? '-'}</dd>
              </div>
              <div>
                <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Klant</dt>
                <dd className="text-sm font-bold text-td-navy mt-0.5">{projectDetail.saleOrder?.partner_id ? projectDetail.saleOrder.partner_id[1] : '-'}</dd>
              </div>
              <div>
                <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Adres</dt>
                <dd className="text-sm font-bold text-td-navy mt-0.5">{projectDetail.werfLocation || werfLocation || '-'}</dd>
              </div>
            </dl>
          </div>

          {/* Task descriptions */}
          <InterventieTaskDescriptions projectDetail={projectDetail} activeTaskId={routeTaskId ?? null} />
        </div>
      )}

      {/* Floor plans */}
      {plans.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <h2 className="text-xs font-bold text-td-muted uppercase tracking-wide mb-3">Verlegschema's</h2>
          <div className="space-y-2">
            {plans.map((plan) => {
              const filename = plan.key.split('/').pop() || plan.key;
              return (
                <div key={plan.key} className="flex items-center gap-3 rounded-lg border border-gray-100 p-3">
                  <svg className="w-5 h-5 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm font-semibold text-td-navy truncate flex-1 min-w-0">{filename}</span>
                  <a
                    href={plan.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 px-3 py-1.5 text-xs font-bold text-white bg-td-blue rounded-lg hover:bg-td-blue/90 transition-colors min-h-[36px] flex items-center"
                  >
                    Downloaden
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Interventie photos — the server resolves every path segment (fase id) from Odoo. */}
      {photosFirst ? null : routeTaskId && routeTaskName ? (
        <InterventieTaskPhotoSection taskId={routeTaskId} />
      ) : !routeTaskId ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
          <p className="text-sm text-amber-700">
            Navigeer via de takenlijst om foto&apos;s te uploaden.
          </p>
        </div>
      ) : null}
    </div>
  );
}

/* --- Task Descriptions --- */

function InterventieTaskDescriptions({
  projectDetail,
  activeTaskId,
}: {
  projectDetail: ProjectDetail;
  activeTaskId: number | null;
}) {
  let primaryTask = activeTaskId
    ? projectDetail.projectTasks.find((t) => t.id === activeTaskId) ?? null
    : null;

  if (!primaryTask && !activeTaskId) {
    const tasksWithDesc = projectDetail.projectTasks.filter(
      (t) => descriptionHasContent(t.description),
    );
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const pad = (n: number) => String(n).padStart(2, '0');
    const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    const future = tasksWithDesc
      .filter((t) => t.date_deadline && String(t.date_deadline).split(' ')[0] >= todayStr)
      .sort((a, b) => String(a.date_deadline).localeCompare(String(b.date_deadline)));
    primaryTask = future[0] ?? tasksWithDesc[0] ?? null;
  }

  if (!primaryTask) return null;

  const hasDescription = descriptionHasContent(primaryTask.description);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 lg:mb-0">
      <h2 className="text-xs font-bold text-td-muted uppercase tracking-wide mb-3">Taakomschrijving</h2>
      <div>
        <p className="text-sm font-semibold text-td-navy">{primaryTask.name}</p>
        {primaryTask.parent_id && (
          <p className="text-[10px] font-bold text-td-muted mt-0.5">{primaryTask.parent_id[1]}</p>
        )}
        {hasDescription ? (
          <SafeHtml html={primaryTask.description || ''} className="text-sm text-td-navy mt-1.5" />
        ) : (
          <p className="text-sm text-td-muted italic mt-1.5">Geen omschrijving voor deze taak</p>
        )}
      </div>
    </div>
  );
}
